import { DateRange } from './RMChangeLog';

export type SurchargeSuggestionDB = {
  id: string;
  offer_id: string;
  vendor_id: string;
  property_id: string;
  room_type_id: string;
  room_rate_id: string;
  offer_name: string;
  property_name: string;
  room_type_name: string;
  suggested_start_date: string;
  suggested_end_date: string;
  before_price: number;
  suggested_after_price: number;
  suggested_surcharge: number;
  next_cheapest_room_price?: number;
  next_cheapest_room_id?: string;
  score?: number;
  confidence?: string;
  created_at?: string;
  booked?: number;
  allocated?: number;
  actioned_start_date: string;
  actioned_end_date: string;
  actioned_surcharge: number;
  created_by_username?: string;
  created_by_user_id?: string;
  status: 'accepted' | 'rejected';
  note?: string;
  fk_surcharge_date_block_id?: string;
  exp_multiplier?: number;
  exp_actioned_surcharge?: number;
  charter_type?: string;
  booked_sum_minus_two_days?: number;
  allocated_sum_minus_two_days?: number;
  booked_sum_plus_two_days?: number;
  allocated_sum_plus_two_days?: number;
};

export type SuggestionType = 'increase' | 'remove';

export type SurchargeSuggestionBQ = Omit<SurchargeSuggestionDB, 'id'> & {
  days_passed: number;
  days_remaining: number;
  sold_since_last_surcharge: number;
  top_up_since_last_surcharge: number;
  sales_last_3_days: number;
  vendor_surcharge_total: number;
  internal_surcharge_total: number;
  nightly_price_aud: number;
  multiplier: number;
  suggestion_type: SuggestionType;
};

export type LerePaginatedResponse<T> = {
  result: Array<T>;
  timestamp?: number;
  total: number;
};

export type SuggestedActionType = 'surcharge' | 'discount';
export type DistanceCategory = 'very_far' | 'far' | 'close' | 'very_close' | 'unknown';
export type HiddenDepartureStatus = 'not_hidden' | 'partially_hidden' | 'fully_hidden';

export type LereToursPricingSuggestion = {
  suggestion_id: string;
  offer_bk: string;
  offer_name: string;
  offer_salesforce_bk: string | null;
  offer_country_name: string | null;
  departure_name: string;
  total_pax_sold: number;
  max_pax: number;
  tour_total_pax_sold?: number | null;
  tour_total_max_pax?: number | null;
  hidden_departure_status: HiddenDepartureStatus;
  ddop_status: string | null;
  cloned_from_departure_id: string | null;
  cloned_from_departure_name: string | null;
  current_book_rate: number | null;
  hedged_pax: number | null;
  departure_date: string;
  handback_date: string | null;
  leadtime: number;
  offer_url: string | null;
  original_tour_price: number;
  suggested_price_change_percent: number;
  suggested_price_change_amount: number;
  after_price: number;
  suggested_action_type: SuggestedActionType;
  distance_category: DistanceCategory;
  predicted_total_pax: string | null;
};

export type LereToursPricingSuggestionsParams = {
  page: number;
  limit: number;
  offer_bk?: string;
  offer_salesforce_bk?: string;
  suggested_action_type?: SuggestedActionType;
};

export type ToursPricingApplyStatus = 'accepted' | 'rejected';
export type ToursPricingHistoryStatus = 'accepted' | 'rejected';

export type LereToursPricingHistoryRecord = {
  id: string;
  suggestion_id: string;
  offer_bk: string;
  offer_url?: string | null;
  offer_salesforce_bk: string | null;
  offer_name: string;
  departure_date: string;
  departure_name: string;
  suggested_action_type: SuggestedActionType;
  distance_category: DistanceCategory;
  original_tour_price: number;
  suggested_price_change_percent: number;
  after_price: number;
  total_pax_sold: number;
  max_pax: number;
  leadtime: number;
  status: string;
  created_by_user_id: string | null;
  created_by_username: string | null;
  created_at: string | null;
};

export type LereToursPricingHistoryParams = {
  page: number;
  limit: number;
  offer_bk?: string;
  offer_salesforce_bk?: string;
  suggested_action_type?: SuggestedActionType;
  status?: ToursPricingHistoryStatus;
  created_at_start?: string;
  created_at_end?: string;
};

export type LereToursPricingApplyRequest = {
  suggestion_id: string;
  status: ToursPricingApplyStatus;
  offer_bk: string;
  offer_salesforce_bk?: string | null;
  offer_name: string;
  departure_date: string;
  departure_name: string;
  suggested_action_type: SuggestedActionType;
  distance_category: DistanceCategory;
  original_tour_price: number;
  suggested_price_change_percent: number;
  after_price: number;
  total_pax_sold: number;
  max_pax: number;
  leadtime: number;
};

export type LereTabComponentProps = {
  onLereError: (error: Error | unknown) => void;
};

export type ChangeLogFilters = {
  propertyName?: string;
  roomTypeName?: string;
  actionedDateRange?: DateRange;
  status?: string;
  onlyCharters?: CharterFilteringOptions;
  enforceRoomTypeGranularity: boolean;
};
export type SurchargeSuggestionFilters = {
  aggregate?: boolean;
  offerIdEquals?: string;
  propertyNameLike?: string;
  roomTypeNameLike?: string;
  internalSurchargeEquals?: number;
  salesGte?: number;
  allocatedGte?: number;
  suggestionType?: SuggestionType;
  confidence?: ConfidenceScoreTier;
  sortBy?: string;
  ascending?: boolean;
  onlyCharters?: CharterFilteringOptions;
};

export enum ConfidenceScoreTier {
  ANY = 'any',
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
}

export enum CharterFilteringOptions {
  NONE = 'None',
  ONLY_CHARTERS = 'only_charters',
  ONLY_NON_CHARTERS = 'only_non_charters',
}

export type LereAbandonedCarts = {
  result: Array<LereAbandonedCart>;
};

export interface LereAbandonedCart {
  cartId?: string;
  regionCode?: string;
  currencyCode?: string;
  items?: Array<LereAbandonedCartItem>;
  offer_id?: string;
  offer_type?: string;
  creation_time?: number;
  total?: number;
  suggestionType?: SuggestionType;
}

export interface LereAbandonedCartItem {
  offerId?: string;
  checkIn?: string;
  checkOut?: string;
  duration?: number;
  occupancy?: {
    adults: number;
    children: number;
    childrenAge: Array<number>;
  };
}

// Cluster types
export interface Cluster {
  cluster_id: number;
  centroid_latitude: number;
  centroid_longitude: number;
  offer_count: number;
  polygon_geojson: string;
}

export interface ClustersResponse {
  run_id: string;
  clustering_config_id: string;
  created_at: string;
  clusters: Array<Cluster>;
}

export interface ClusterOffer {
  offer_id: string;
  cluster_id: number;
}

export interface ClusterOffersResponse {
  run_id: string;
  offers: Array<ClusterOffer>;
  count: number;
}

export interface OfferRecommendation {
  offer_id: string;
  offer_type: string | null;
  score: number | null;
}

export interface OfferRecommendationsResponse {
  offer_id: string;
  recommendations: Array<OfferRecommendation>;
  count: number;
}

export interface Clustering {
  run_id: string;
  clustering_config_id: string;
  created_at: string;
  algo_version: string;
  params_json: string;
  noise_policy?: string;
}

export interface ManualCluster {
  name: string;
  polygon: Array<LatLongTuple>;
  enabled?: boolean;
  protected?: boolean;
}

export interface ClusteringsResponse {
  clusterings: Array<Clustering>;
  count: number;
}

export interface ClusteringParamsInput {
  cut_km?: number;
  min_cluster_size?: number;
  min_samples?: number;
  max_assign_dist_km?: number;
  manual_clusters?: Array<ManualCluster>;
}

export interface ClusteringRequest {
  params?: ClusteringParamsInput;
  noise_policy?: 'nearest_cluster' | 'leave_noise';
  algo_version?: string;
  data_snapshot_id?: string;
}

export interface ClusteringRunResponse extends ClustersResponse {
  algo_version: string;
  params_json: string;
  noise_policy: string;
  data_snapshot_id?: string | null;
  assignments_count: number;
  noise_count: number;
  noise_pct: number;
}

export type LatLongTuple = [number, number];

export type ClusterOverlay = {
  cluster: Cluster;
  color: string;
  tooltipLabel: string;
  paths: Array<Array<LatLongTuple>>;
};

export type ClusteringRunParams = {
  cut_km?: number;
  min_cluster_size?: number;
  min_samples?: number;
  max_assign_dist_km?: number;
  manual_clusters?: Array<ManualCluster>;
  merge_regions?: Array<ManualCluster>;
  raw_params_json?: string;
  [key: string]: unknown;
};

export type NoiseSummary = {
  noiseCount: number;
  totalPoints: number;
  noisePct: number;
};

export type ManualClusterValidation = {
  hasPolygon: boolean;
  polygonInRange: boolean;
};
